import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Send, ChevronLeft, Check, X as XIcon, ShieldAlert, ShoppingBag, Star } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";
import { useAuth } from "../context/AuthContext";
import api, { fileUrl } from "../lib/api";
import { timeAgo } from "../lib/format";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { SaleReviewModal } from "../components/modals";
import { toast } from "sonner";

export default function Messages() {
  const { t, lang } = useI18n();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [convs, setConvs] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [blocked, setBlocked] = useState(null); // { reason, strike }
  const [suspended, setSuspended] = useState(false);
  const [pendingReviews, setPendingReviews] = useState([]);
  const [reviewSale, setReviewSale] = useState(null);
  const listRef = useRef();
  const scrolledConv = useRef(null);
  const focusConv = searchParams.get("conv");

  useEffect(() => { if (user === false) navigate("/auth"); }, [user, navigate]);

  const loadPending = useCallback(async () => {
    try { const { data } = await api.get("/sales/pending-reviews"); setPendingReviews(data || []); } catch {}
  }, []);
  useEffect(() => { loadPending(); }, [loadPending]);

  const loadConvs = useCallback(async () => {
    try { const { data } = await api.get("/conversations", { params: { lang } }); setConvs(data); } catch {}
  }, [lang]);
  useEffect(() => { loadConvs(); const iv = setInterval(loadConvs, 8000); return () => clearInterval(iv); }, [loadConvs]);

  useEffect(() => {
    if (focusConv && convs.length) {
      const c = convs.find((x) => x.id === focusConv);
      if (c) setActive(c);
    }
  }, [focusConv, convs]);

  const loadMessages = useCallback(async (cid) => {
    try { const { data } = await api.get(`/conversations/${cid}/messages`, { params: { lang } }); setMessages(data); } catch {}
  }, [lang]);

  const msgText = useCallback((m) => {
    const i = m.meta?.i18n;
    if (i?.key) {
      const tpl = t(`chatmsg.${i.key}`);
      if (typeof tpl === "string") return tpl.replace(/\{(\w+)\}/g, (_, k) => (i.params?.[k] ?? ""));
    }
    return m.text;
  }, [t]);
  useEffect(() => {
    if (!active) return;
    loadMessages(active.id);
    const iv = setInterval(() => loadMessages(active.id), 5000);
    return () => clearInterval(iv);
  }, [active, loadMessages]);

  // Jump to the latest message only once when a conversation is opened. No auto-scroll on
  // polling refreshes or while typing — keeps the view fully static on desktop and mobile.
  useEffect(() => { scrolledConv.current = null; }, [active?.id]);
  useEffect(() => {
    if (active && scrolledConv.current !== active.id && messages.length) {
      const el = listRef.current;
      if (el) el.scrollTop = el.scrollHeight;
      scrolledConv.current = active.id;
    }
  }, [active, messages]);

  const send = async () => {
    if (!text.trim() || !active?.other_user) return;
    const body = { listing_id: active.listing?.id || null, receiver_id: active.other_user.id, text };
    try {
      await api.post("/messages", body);
      setText("");
      loadMessages(active.id);
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 422 && d?.code === "message_blocked") {
        if (d.suspended) setSuspended(true);
        else setBlocked({ reason: d.reason || "offsite", strike: d.strike || 1 });
      } else if (e.response?.status === 403) {
        setSuspended(true);
      } else toast.error("Error");
    }
  };

  const offerAction = async (offerId, action) => {
    let counter_amount = null;
    if (action === "counter") { const v = prompt(t("offer.counterAmount")); if (!v) return; counter_amount = Number(v); }
    await api.post(`/offers/${offerId}/action`, { action, counter_amount });
    toast.success("OK"); loadMessages(active.id); loadConvs();
  };

  const chatMakeOffer = async () => {
    if (!active?.listing) return;
    const v = prompt(t("offer.counterAmount"));
    const amount = Number(v);
    if (!v || !amount || amount <= 0) return;
    try {
      await api.post("/offers", { listing_id: active.listing.id, amount, message: "" });
      toast.success("OK"); loadMessages(active.id);
    } catch { toast.error("Error"); }
  };

  const OFFER_STATUS = {
    pending: { es: "En espera", en: "Pending", it: "In attesa" },
    accepted: { es: "Aceptada", en: "Accepted", it: "Accettata" },
    rejected: { es: "Rechazada", en: "Rejected", it: "Rifiutata" },
    countered: { es: "Contraoferta", en: "Countered", it: "Controproposta" },
  };
  const offerStatusLabel = (s) => OFFER_STATUS[s]?.[lang] || OFFER_STATUS[s]?.es || s;
  const statusChip = (s) => s === "accepted" ? "bg-emerald-100 text-emerald-700"
    : s === "rejected" ? "bg-rose-100 text-rose-700"
    : s === "countered" ? "bg-indigo-100 text-indigo-700" : "bg-amber-100 text-amber-700";
  const isBuyerActive = active?.listing && user?.id !== active.listing.seller_id;
  const activeReview = active?.listing ? pendingReviews.find((r) => r.listing_id === active.listing.id) : null;

  return (
    <div className="px-0 sm:px-8 py-0 sm:py-6">
      <div className="bg-white sm:border sm:border-slate-200 sm:rounded-3xl overflow-hidden flex h-[calc(100vh-4rem)] sm:h-[calc(100vh-8rem)]">
        {/* Conversation list */}
        <div className={`w-full sm:w-80 border-r border-slate-200 flex-col ${active ? "hidden sm:flex" : "flex"}`}>
          <div className="p-4 border-b border-slate-100"><h1 className="font-display font-bold text-xl">{t("chat.title")}</h1></div>
          <div className="flex-1 overflow-y-auto">
            {convs.length === 0 ? (
              <div className="p-8 text-center text-sm text-slate-400">{t("chat.noConversations")}</div>
            ) : convs.map((c) => (
              <button key={c.id} data-testid={`conv-${c.id}`} onClick={() => setActive(c)}
                className={`w-full flex items-center gap-3 p-3 text-left hover:bg-slate-50 border-b border-slate-50 ${active?.id === c.id ? "bg-teal-50/50" : ""}`}>
                <div className="relative shrink-0">
                  <Avatar className="h-11 w-11"><AvatarImage src={fileUrl(c.other_user?.profile_photo)} /><AvatarFallback className="bg-teal-100 text-teal-700">{c.other_user?.name?.[0]}</AvatarFallback></Avatar>
                  {c.listing && (
                    <span className="absolute -bottom-1 -right-1 h-6 w-6 rounded-md overflow-hidden ring-2 ring-white bg-slate-200 grid place-items-center">
                      {c.listing.photos?.[0]
                        ? <img src={fileUrl(c.listing.photos[0])} alt="" className="h-full w-full object-cover" />
                        : <ShoppingBag className="h-3 w-3 text-slate-400" />}
                    </span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{c.other_user?.name}</p>
                  {c.listing?.title && <p data-testid={`conv-listing-${c.id}`} className="text-xs text-slate-500 truncate">{c.listing.title}</p>}
                  <p className="text-xs text-slate-400 truncate">{c.last_message}</p>
                </div>
                {c.unread > 0 && <span className="h-5 min-w-5 px-1 grid place-items-center text-[10px] font-bold text-white bg-[#008080] rounded-full">{c.unread}</span>}
              </button>
            ))}
          </div>
        </div>

        {/* Thread */}
        <div className={`flex-1 flex-col ${active ? "flex" : "hidden sm:flex"}`}>
          {!active ? (
            <div className="flex-1 grid place-items-center text-slate-400 text-sm">{t("chat.selectConversation")}</div>
          ) : (
            <>
              <div className="p-2 border-b border-slate-100 flex items-center gap-2 sticky top-0 z-10 bg-white">
                <button className="sm:hidden shrink-0" onClick={() => setActive(null)}><ChevronLeft className="h-5 w-5" /></button>
                <button data-testid="chat-user-header" onClick={() => active.other_user?.id && navigate(`/user/${active.other_user.id}`)}
                  className="flex-1 min-w-0 flex items-center gap-3 rounded-xl px-2 py-1.5 hover:bg-slate-50 transition-colors text-left">
                  <Avatar className="h-9 w-9"><AvatarImage src={fileUrl(active.other_user?.profile_photo)} /><AvatarFallback className="bg-teal-100 text-teal-700">{active.other_user?.name?.[0]}</AvatarFallback></Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{active.other_user?.nickname ? `@${active.other_user.nickname}` : active.other_user?.name}</p>
                    <span className="flex items-center gap-1 text-xs text-slate-500">
                      <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
                      {(active.other_user?.rating || 0).toFixed(1)}
                      {active.other_user?.review_count ? <span className="text-slate-400">({active.other_user.review_count})</span> : null}
                    </span>
                  </div>
                  <ChevronLeft className="h-4 w-4 text-slate-300 rotate-180 shrink-0" />
                </button>
              </div>
              {active.listing && (
                <button data-testid="chat-product-header" onClick={() => navigate(`/listing/${active.listing.id}`)}
                  className="flex items-center gap-3 p-2.5 border-b border-slate-100 bg-slate-50 hover:bg-slate-100 transition-colors text-left">
                  <div className="h-12 w-12 rounded-xl overflow-hidden bg-slate-200 shrink-0">
                    {active.listing.photos?.[0]
                      ? <img src={fileUrl(active.listing.photos[0])} alt="" className="h-full w-full object-cover" />
                      : <div className="h-full w-full grid place-items-center text-slate-400"><ShoppingBag className="h-5 w-5" /></div>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{active.listing.title}</p>
                    <p className="text-sm font-bold text-[#008080]">{active.listing.price} €</p>
                  </div>
                  <ChevronLeft className="h-4 w-4 text-slate-300 rotate-180" />
                </button>
              )}
              <div ref={listRef} className="flex-1 overflow-y-auto p-4 space-y-2 bg-slate-50/50">
                {messages.map((m) => {
                  const mine = m.sender_id === user?.id;
                  const isSeller = active.listing && user?.id === active.listing.seller_id;
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[75%] px-3 py-2 rounded-2xl text-sm ${m.type === "system" ? "bg-amber-50 text-amber-800 text-xs mx-auto" : mine ? "bg-[#008080] text-white" : "bg-white border border-slate-200"}`}>
                        <p className="whitespace-pre-line">{msgText(m)}</p>
                        {(m.type === "offer" || m.type === "offer_counter") && m.meta?.status && (
                          <span data-testid="offer-status" className={`inline-block mt-1 text-[10px] font-semibold px-2 py-0.5 rounded-full ${statusChip(m.meta.status)}`}>{offerStatusLabel(m.meta.status)}</span>
                        )}
                        {m.type === "offer" && isSeller && m.meta?.offer_id && m.meta?.status === "pending" && (
                          <div className="flex gap-1 mt-2">
                            <button data-testid="accept-offer" onClick={() => offerAction(m.meta.offer_id, "accept")} className="flex-1 text-xs bg-emerald-500 text-white rounded-lg py-1"><Check className="h-3 w-3 inline" /> {t("offer.accept")}</button>
                            <button data-testid="reject-offer" onClick={() => offerAction(m.meta.offer_id, "reject")} className="flex-1 text-xs bg-rose-500 text-white rounded-lg py-1"><XIcon className="h-3 w-3 inline" /> {t("offer.reject")}</button>
                            <button data-testid="counter-offer" onClick={() => offerAction(m.meta.offer_id, "counter")} className="flex-1 text-xs bg-slate-600 text-white rounded-lg py-1">{t("offer.counter")}</button>
                          </div>
                        )}
                        {m.type === "offer_counter" && !isSeller && m.meta?.offer_id && m.meta?.status === "countered" && (
                          <div className="flex gap-1 mt-2">
                            <button data-testid="accept-counter" onClick={() => offerAction(m.meta.offer_id, "accept")} className="flex-1 text-xs bg-emerald-500 text-white rounded-lg py-1"><Check className="h-3 w-3 inline" /> {t("offer.accept")}{m.meta.amount ? ` ${m.meta.amount} €` : ""}</button>
                            <button data-testid="reject-counter" onClick={() => offerAction(m.meta.offer_id, "reject")} className="flex-1 text-xs bg-rose-500 text-white rounded-lg py-1"><XIcon className="h-3 w-3 inline" /> {t("offer.reject")}</button>
                          </div>
                        )}
                        {m.type === "offer_accepted" && m.meta?.listing_id && (
                          <p data-testid="offer-accepted-info" className="mt-2 text-xs font-semibold text-emerald-700">{t("offer.acceptedAgree")}{m.meta.amount ? ` · ${m.meta.amount} €` : ""}</p>
                        )}
                        <p className={`text-[10px] mt-1 ${mine ? "text-teal-100" : "text-slate-400"}`}>{timeAgo(m.created_at, lang)}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
              {isBuyerActive && active.listing.status !== "vendido" && (
                <div className="px-3 pt-2 flex gap-2">
                  <Button data-testid="chat-counter-bar" onClick={chatMakeOffer} variant="outline"
                    className="flex-1 h-10 border-[#008080] text-[#008080] text-sm font-semibold hover:bg-teal-50">
                    {t("offer.makeCounter")}
                  </Button>
                </div>
              )}
              {activeReview && (
                <div className="px-3 pt-2">
                  <Button data-testid="chat-review-bar" onClick={() => setReviewSale(activeReview)}
                    className="w-full h-10 bg-[#FF9F1C] hover:bg-[#e88f13] text-white text-sm font-semibold">
                    <Star className="h-4 w-4 mr-1" />{t("saleReview.cta")}
                  </Button>
                </div>
              )}
              <div className="p-3 border-t border-slate-100 flex items-center gap-2">
                <input data-testid="message-input" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
                  placeholder={t("chat.typeMessage")} className="flex-1 bg-slate-100 rounded-full px-4 h-11 outline-none text-sm" />
                <Button data-testid="send-message-btn" onClick={send} className="h-11 w-11 p-0 rounded-full bg-[#008080] hover:bg-[#006666]"><Send className="h-4 w-4" /></Button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Blocked message warning (progressive) */}
      <Dialog open={!!blocked} onOpenChange={(o) => { if (!o) setBlocked(null); }}>
        <DialogContent className="rounded-2xl" data-testid="chat-blocked-modal">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2 text-rose-600">
              <span className="h-8 w-8 grid place-items-center rounded-lg bg-rose-500 text-white"><ShieldAlert className="h-4 w-4" /></span>
              {t("chatSafety.blockedTitle")}
            </DialogTitle>
          </DialogHeader>
          {blocked && (
            <div className="space-y-3 text-sm text-slate-600">
              <p className="font-medium text-slate-800">{t(`chatSafety.reasons.${blocked.reason}`)}</p>
              <p>{t("chatSafety.blocked")}</p>
              <div className={`rounded-lg border p-3 text-xs flex items-start gap-2 ${blocked.strike >= 2 ? "bg-rose-100 border-rose-300 text-rose-800 font-semibold" : "bg-amber-50 border-amber-200 text-amber-800"}`}>
                <ShieldAlert className="h-4 w-4 shrink-0 mt-0.5" />
                <span data-testid="chat-strike-warning">{blocked.strike >= 2 ? t("chatSafety.strike2") : t("chatSafety.strike1")}</span>
              </div>
            </div>
          )}
          <Button data-testid="chat-blocked-ack-btn" onClick={() => setBlocked(null)} className="w-full h-11 bg-[#008080] hover:bg-[#006666]">{t("chatSafety.ack")}</Button>
        </DialogContent>
      </Dialog>

      {/* Account suspended */}
      <Dialog open={suspended} onOpenChange={() => {}}>
        <DialogContent className="rounded-2xl" data-testid="chat-suspended-modal">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2 text-rose-600">
              <span className="h-8 w-8 grid place-items-center rounded-lg bg-rose-600 text-white"><ShieldAlert className="h-4 w-4" /></span>
              {t("chatSafety.suspendedTitle")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm text-slate-600">
            <p>{t("chatSafety.suspendedText")}</p>
          </div>
          <Button data-testid="chat-suspended-ack-btn" onClick={async () => { await logout(); navigate("/auth"); }} className="w-full h-11 bg-rose-600 hover:bg-rose-700">{t("chatSafety.ack")}</Button>
        </DialogContent>
      </Dialog>

      <SaleReviewModal open={!!reviewSale} onOpenChange={(o) => { if (!o) setReviewSale(null); }} sale={reviewSale}
        onDone={() => { setReviewSale(null); loadPending(); }} />
    </div>
  );
}
